import React, { useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { Alert, Button, Checkbox, Modal, Plus, TextInput, Textarea } from "@opendate-app/setlist";
import { toastSuccess } from "../../shared/toastHelper";
import focusField from "../ClassesShared/focusField";
import useFocusAfterRemove from "../ClassesShared/useFocusAfterRemove";
import "../ClassesShared/classesShared.css";

const blankAttendee = () => ({ first_name: "", last_name: "", email: "", phone: "", note_to_instructor: "" });
const REQUIRED_FIELDS = [["first_name", "Enter a first name."], ["last_name", "Enter a last name."], ["email", "Enter an email."]];

function AddComplimentaryRegistrationsModal({ path, csrfToken, spotsLeft, maxPerOrder, onClose, onAdded }) {
  const [attendees, setAttendees] = useState([blankAttendee()]);
  const [compReason, setCompReason] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [working, setWorking] = useState(false);
  const [errors, setErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [focusRequest, setFocusRequest] = useState(null);
  const focusAfterRemove = useFocusAfterRemove();

  useEffect(() => {
    if (focusRequest) focusField(focusRequest.field);
  }, [focusRequest]);

  const maxAttendees = Math.min(spotsLeft, maxPerOrder);
  const canAddRow = attendees.length < maxAttendees;

  const update = (index, field, value) => {
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[`attendee-${index}-${field}`];
      return next;
    });
    setAttendees((rows) => rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)));
  };

  const removeAttendee = (index) => {
    setFieldErrors({});
    focusAfterRemove("[data-attendee-remove]", index, "[data-field=\"attendee-0-first_name\"]");
    setAttendees((rows) => rows.filter((_, rowIndex) => rowIndex !== index));
  };

  const missingFields = () => {
    const missing = {};
    attendees.forEach((attendee, index) => {
      REQUIRED_FIELDS.forEach(([field, message]) => {
        if (!attendee[field].trim()) missing[`attendee-${index}-${field}`] = message;
      });
    });
    return missing;
  };

  const submit = async () => {
    if (working) return;
    const missing = missingFields();
    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      setFocusRequest({ field: Object.keys(missing)[0] });
      return;
    }
    setWorking(true);
    setErrors([]);
    try {
      await axios.post(
        path,
        { complimentary_class_registration: { attendees, comp_reason: compReason, should_send_ticket_email: sendEmail } },
        { headers: { "X-CSRF-Token": csrfToken } },
      );
      toastSuccess(attendees.length === 1 ? "Registration added." : `${attendees.length} registrations added.`);
      onAdded();
    } catch (e) {
      setErrors(e.response?.data?.errors?.base || ["Could not add the registrations."]);
      setWorking(false);
    }
  };

  const footer = (
    <div className="classes-modal-footer">
      <Button variant="secondary" onClick={onClose} disabled={working}>Cancel</Button>
      <Button variant="primary" onClick={submit} aria-disabled={working || undefined}>
        {working ? "Adding…" : `Add ${attendees.length === 1 ? "Registration" : `${attendees.length} Registrations`}`}
      </Button>
    </div>
  );

  return (
    <Modal open onClose={onClose} icon={false} title="Add Registration" footer={footer}>
      <p className="mb-3 classes-text-secondary">
        {`Added registrations are complimentary: each attendee takes a spot at no charge. ${spotsLeft} ${spotsLeft === 1 ? "spot" : "spots"} left.`}
      </p>
      {errors.length > 0 && (
        <div className="mb-3"><Alert color="error" role="alert" description={errors.join(" ")} /></div>
      )}
      {attendees.map((attendee, index) => (
        <div key={index} className="border rounded p-3 mb-3" data-testid="attendee-row" role="group" aria-labelledby={`add-registration-attendee-${index}`}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong id={`add-registration-attendee-${index}`}>{`Attendee ${index + 1}`}</strong>
            {attendees.length > 1 && (
              <Button variant="tertiary" aria-label={`Remove attendee ${index + 1}`} data-attendee-remove onClick={() => removeAttendee(index)}>
                Remove
              </Button>
            )}
          </div>
          <div className="d-flex flex-wrap classes-gap-12">
            <div className="flex-grow-1"><TextInput data-field={`attendee-${index}-first_name`} label="First name" required error={fieldErrors[`attendee-${index}-first_name`]} value={attendee.first_name} onChange={(e) => update(index, "first_name", e.target.value)} /></div>
            <div className="flex-grow-1"><TextInput data-field={`attendee-${index}-last_name`} label="Last name" required error={fieldErrors[`attendee-${index}-last_name`]} value={attendee.last_name} onChange={(e) => update(index, "last_name", e.target.value)} /></div>
          </div>
          <div className="d-flex flex-wrap mt-2 classes-gap-12">
            <div className="flex-grow-1"><TextInput data-field={`attendee-${index}-email`} label="Email" required type="email" error={fieldErrors[`attendee-${index}-email`]} value={attendee.email} onChange={(e) => update(index, "email", e.target.value)} /></div>
            <div className="flex-grow-1"><TextInput label="Phone" value={attendee.phone} onChange={(e) => update(index, "phone", e.target.value)} /></div>
          </div>
          <div className="mt-2">
            <Textarea label="Note to instructor" rows={2} value={attendee.note_to_instructor} onChange={(e) => update(index, "note_to_instructor", e.target.value)} />
          </div>
        </div>
      ))}
      {canAddRow && (
        <Button variant="secondary" icon={Plus} onClick={() => setAttendees((rows) => [...rows, blankAttendee()])}>
          Add Another Attendee
        </Button>
      )}
      <div className="mt-3">
        <TextInput label="Comp reason" placeholder="Optional, e.g. press or staff guest" maxLength={255} value={compReason} onChange={(e) => setCompReason(e.target.value)} />
      </div>
      <div className="mt-3">
        <Checkbox
          id="complimentary-registration-send-email"
          data-testid="complimentary-registration-send-email"
          label="Send confirmation email"
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
        />
        <p className="text-muted small mt-1 mb-0">Each attendee gets their schedule, a calendar file, a link to view their registration, and a reminder before each session.</p>
      </div>
    </Modal>
  );
}

AddComplimentaryRegistrationsModal.propTypes = {
  path: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  spotsLeft: PropTypes.number.isRequired,
  maxPerOrder: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdded: PropTypes.func.isRequired,
};

export default AddComplimentaryRegistrationsModal;
